import type { Pitch, PitchStatus, Talent, User } from "../../shared/types";
import { isHiddenCampaignPitch, type CampaignPitchingState, type CampaignReviewContext } from "../../utils/pitching/permissions";

export type DetailTab = "pitches" | "bookings";
export type ConfirmKind = "open" | "close" | null;

export type PitchRow = Pitch & {
    talent?: Talent;
};

/** Pitches this viewer may see. Rejected and withdrawn hide unless reviewer, pitcher, or on the creator. */
export function visiblePitches<T extends Pick<Pitch, "status" | "managerId"> & { talent?: Pick<Talent, "managerIds"> }>(
    user: User | undefined,
    campaign: CampaignReviewContext,
    pitches: readonly T[],
): T[] {
    return pitches.filter((pitch) => !isHiddenCampaignPitch(user, campaign, pitch, pitch.talent));
}

/** Pitches when pitching is open or any visible pitch exists; otherwise Bookings. */
export function defaultDetailTab(
    user: User | undefined,
    campaign: CampaignReviewContext & CampaignPitchingState,
    pitches: readonly Pick<Pitch, "status" | "managerId">[],
): DetailTab {
    const isOpen = Boolean(campaign.pitchingOpen);
    const hasVisiblePitch = visiblePitches(user, campaign, pitches).length > 0;
    return isOpen || hasVisiblePitch ? "pitches" : "bookings";
}

/** Pitches in this list whose status matches the Reject remaining checkboxes. */
export function pitchesToReject<T extends Pick<Pitch, "status">>(
    pitches: readonly T[],
    rejectPitched: boolean,
    rejectNeedsInfo: boolean,
): T[] {
    const statuses: PitchStatus[] = [];
    if (rejectPitched) {
        statuses.push("pitched");
    }
    if (rejectNeedsInfo) {
        statuses.push("needs-info");
    }
    return pitches.filter((pitch) => pitch.status && statuses.includes(pitch.status));
}
